import React from 'react';
import {
  VoucherExperience,
  VoucherGroundService,
  VoucherTransfer,
  BMSVoucherGuest,
  BMSVoucherHideableText,
} from 'services/ItineraryManagerApi/types';
import { formatDateDisplay, isBlank } from 'utils';
import { SvgIcon } from 'ui/SvgIcon';
import PersonIcon from 'ui/Icons/person-circle.component.svg';

const formatTime = (time?: string) => {
  if (!time) return '-';
  return time;
};

/**
 * Helper function to determine if a BMSVoucherHideableText field should be rendered
 * @param hideableText - The hideable text object to check
 * @returns true if the field should be rendered (is visible and has non-empty text)
 */

export const shouldRenderHideableText = (
  hideableText?: BMSVoucherHideableText | null
): boolean => {
  return !!(hideableText?.isVisible && hideableText.text.trim() !== '');
};

export const ProductViewOverFold = ({
  product,
}: {
  product: VoucherTransfer | VoucherGroundService | VoucherExperience;
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[70%_30%] mb-5px gap-5px">
      <span className="font-pt-sans font-bold text-17px text-black block">{product.title}</span>
      <span className="md:text-right">
        {!isBlank(product.confirmationNumber?.trim()) && (
          <span className="bg-white-hard text-gray-100 font-pt-sans py-3px">Confirm N. {product.confirmationNumber}</span>
        )}
      </span>
      <span className="md:order-2 md:text-right font-pt-sans text-black text-15px">
        {product.date && formatDateDisplay(product.date)}
        {product.time && ` at ${formatTime(product.time)}`}
      </span>
      <span className="md:order-1 font-pt-sans text-black text-15px">{product.tertiaryText}</span>
    </div>
  );
};

export const ProductEditUnderFold = ({
  product,
}: {
  product: VoucherTransfer | VoucherGroundService | VoucherExperience;
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[70%_30%] mb-5px gap-5px">
      <span className="row-span-2 self-end text-black font-pt-sans text-15px">{product.tertiaryText}</span>
      <span className="md:text-right">
        {!isBlank(product.confirmationNumber?.trim()) && (
          <span className="text-gray-100 bg-white-hard py-1 text-13px">Confirm N. {product.confirmationNumber}</span>
        )}
      </span>
      <span className="md:text-right text-black text-15px font-pt-sans">
        {product.date && formatDateDisplay(product.date)}
        {product.time && ` at ${formatTime(product.time)}`}
      </span>
    </div>
  );
};

interface IAllocatedGuestsProps {
  allocations: { guestUuid: string; breakdownItemUuid: string }[];
  guests: BMSVoucherGuest[];
  productUuid: string;
}

export const AllocatedGuests: React.FC<IAllocatedGuestsProps> = ({ allocations, guests, productUuid }) => {
  // Filter allocations for this specific product
  const productAllocations = allocations.filter(allocation => allocation.breakdownItemUuid === productUuid);

  if (productAllocations.length === 0) return null;

  // Get the guest details for each allocation
  const allocatedGuests = productAllocations
    .map(allocation => guests.find(guest => guest.uuid === allocation.guestUuid))
    .filter((guest): guest is BMSVoucherGuest => guest !== undefined && guest.isVisible);

  if (allocatedGuests.length === 0) return null;

  return (
    <div className="mt-3">
      <p className="m-0 font-pt-sans text-15px text-brown-prime mb-2">
        <span className="font-bold">Allocated Guests</span> ({allocatedGuests.length})
      </p>
      <div className="flex flex-wrap gap-20px">
        {allocatedGuests.map(guest => (
          <div key={guest.uuid} className="flex items-center gap-5px">
            <SvgIcon IconComponent={PersonIcon} className="fill-gray-40" width="16px" height="16px" />
            <span className="font-pt-sans text-15px text-black">
              {guest.firstName} {guest.lastName}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
